import { useMutation } from "@tanstack/react-query"
import classNames from "classnames"
import { Button } from "components/LEGACY/styled-components/Buttons"
import Tiptap from "components/editor/Tiptap"
import ProductSelector from "components/marketing/admin/Newsletter/NewNewsletterProductSelector"
import SelectedProducts from "components/marketing/admin/Newsletter/SelectedProducts"
import InputField from "components/utils/InputField"
import { addDoc, updateDoc } from "firebase/firestore"
import { getBroadcastRef, getBroadcastsRef } from "hooks/firestore/getRefs"
import { useBroadcast } from "hooks/firestore/simple/useBroadcast"
import { useShop } from "hooks/firestore/simple/useShop"
import { useUser } from "hooks/firestore/simple/useUser"
import { useDisplayToast } from "hooks/misc/useDisplayToast"
import { MouseEvent, useEffect, useState } from "react"
import styled from "styled-components"
import { Step } from "styled-icons/fluentui-system-filled"
import { Broadcast } from "types/firestore/broadcast"
import { functions } from "../../../../firebase/firebase"

const Form = styled.form`
  display: grid;
  grid-gap: 1rem;
`

interface Step {
  id: number
  content: React.ReactNode // Can be a string, JSX, or component
  condition?: boolean
  successCondition?: boolean
  renderContent?: (step: Step) => React.ReactNode // Optional, for complex rendering
}

export default function NewNewsletter({
  backToOverview,
  editingNewsletterId,
}: {
  backToOverview: () => void
  editingNewsletterId?: string | null
}) {
  const user = useUser()
  const shop = useShop()
  const broadcastRef = getBroadcastsRef(shop?._ref.id)
  const editingBroadcast = useBroadcast(editingNewsletterId ?? undefined)

  const [subject, setSubject] = useState("")
  const [header, setHeader] = useState("")
  const [body, setBody] = useState({
    html: "",
    text: "",
  })
  const [offerHeader, setOfferHeader] = useState("")
  const [productIds, setProductIds] = useState<string[]>([])
  const [status, setStatus] = useState("default")
  const userEmail = user?.userName
  const [loading, setLoading] = useState(false)
  const [emailType, setEmailType] = useState<"products" | "text-only">("text-only")
  const [scheduledTime, setScheduledTime] = useState<string | null>(null)

  const broadcastNewsletter = async ({
    testEmail = "",
    scheduledTimeUnix = null,
  }: {
    testEmail?: string
    scheduledTimeUnix?: number | null
  }) => {
    if (!shop) {
      throw new Error("No shop loaded for newsletter")
    }

    const broadcast = await functions.httpsCallable("groupNL-broadcastNewsletter")({
      shopId: shop._ref.id,
      mail: {
        subject,
        header,
        body,
        offerHeader,
      },
      testEmail,
      productIds,
      draftNewsletterId: editingNewsletterId,
      scheduledTimeUnix,
    })
    return broadcast
  }

  useEffect(() => {
    if (editingBroadcast) {
      setSubject(editingBroadcast.subject)
      setHeader(editingBroadcast.header ?? "")
      setBody(
        typeof editingBroadcast.body === "string" ? { text: editingBroadcast.body, html: "" } : editingBroadcast.body
      )
      setOfferHeader(editingBroadcast.offerHeader ?? "")
      setProductIds(editingBroadcast.productIds)
    }
  }, [editingBroadcast])

  const sendTestMail = async (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    if (!userEmail) return
    setLoading(true)
    await broadcastNewsletter({ testEmail: userEmail })
    setLoading(false)
    setStatus("tested")
  }

  const sendNewsletter = async (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    setLoading(true)
    const scheduledTimeDate = scheduledTime ? new Date(scheduledTime) : null
    const unixTimestamp = scheduledTimeDate ? Math.floor(scheduledTimeDate.getTime() / 1000) : null
    await broadcastNewsletter({ scheduledTimeUnix: unixTimestamp })
    setStatus("sent")
    setLoading(false)
  }

  const allFieldsFilled =
    emailType === "products"
      ? !!offerHeader && !!body.text && !!header && !!subject
      : !!body.text && !!body.html && !!subject

  useEffect(() => {
    if (status === "sent") {
      setTimeout(() => {
        backToOverview()
      }, 2500)
    }
  }, [status])

  const steps: Step[] = [
    {
      id: 1,
      condition: true, // Always true, so this step is always shown.
      content: allFieldsFilled ? "Alle Felder ausgefüllt ✓" : "Bitte fülle alle Felder aus",
      successCondition: allFieldsFilled,
    },
    {
      id: 2,
      condition: emailType === "products",
      content: productIds.length > 0 ? "Produkte sind gewählt ✓" : "Bitte wähle 1-5 Produkte oben aus",
      successCondition: productIds.length > 0,
    },
    {
      id: 3,
      condition: true, // Assuming you always want to show this step, but its content changes based on the status.
      content: (
        <>
          {status === "succeededTest" || status === "tested" || status === "sent" ? (
            `Test gesendet wurde an ${userEmail} gesendet ✓`
          ) : (
            <>
              Sende dir selbst einen Test
              <div className="stepButtonContainer">
                <Button onClick={sendTestMail} disabled={!allFieldsFilled}>
                  {loading ? "Bitte warten..." : "Mail testen"}
                </Button>
              </div>
            </>
          )}
        </>
      ),
      successCondition: status === "succeededTest" || status === "tested" || status === "sent",
    },
    {
      id: 4,
      condition: true,
      content: (
        <>
          {status === "succeededTest" || status === "sent" ? (
            "Test als Erfolg markiert gesendet ✓"
          ) : (
            <>
              Prüfe den Test
              {status === "tested" && (
                <div className="stepButtonContainer">
                  <Button danger onClick={() => setStatus("default")}>
                    Nochmal bearbeiten
                  </Button>
                  <Button success onClick={() => setStatus("succeededTest")}>
                    Test erfolgreich
                  </Button>
                </div>
              )}
            </>
          )}
        </>
      ),
      successCondition: status === "succeededTest" || status === "sent",
    },
    {
      id: 5,
      condition: true,
      content: (
        <>
          {status === "sent" ? (
            "Newsletter gesendet ✓"
          ) : (
            <>
              Versende den Newsletter
              {status === "succeededTest" && (
                <div className="stepButtonContainer">
                  <Button ghost onClick={() => setStatus("default")}>
                    Nochmal bearbeiten
                  </Button>
                  <Button onClick={sendNewsletter} className="tw-mt-1">
                    {loading ? "Bitte warten..." : "An alle Abonnenten versenden"}
                  </Button>
                  <input
                    className="tw-mt-2"
                    type="datetime-local"
                    onChange={e => setScheduledTime(e.target.value)}
                    disabled={status !== "succeededTest"}
                  />
                </div>
              )}
            </>
          )}
        </>
      ),
      successCondition: status === "sent",
    },
  ]

  const displayToast = useDisplayToast()

  // When switching to the new view, the steps are reset
  useEffect(() => {
    setStatus("default")
  }, [emailType])

  const saveDraftMutation = useMutation({
    mutationFn: async (newsletterDraft: Broadcast) => {
      if (!shop) throw new Error("Shop not found")

      const shopId = shop._ref.id
      if (editingNewsletterId) {
        await updateDoc(getBroadcastRef(shopId, editingNewsletterId), newsletterDraft)
      } else {
        await addDoc(getBroadcastsRef(shopId), newsletterDraft)
      }
    },
    onSuccess: () => {
      displayToast("Newsletter als Entwurf gespeichert", "success")
    },
    onError: err => {
      console.error("error", err)
      displayToast("Fehler beim Speichern des Newsletters", "error")
    },
  })

  const handleSaveDraft = () => {
    if (!shop) return

    const newsletterDraft: Broadcast = {
      body,
      header,
      offerHeader,
      productIds,
      subject,
      isDraft: true,
      log: "",
      recipients: 0,
      sentAt: null,
    }

    saveDraftMutation.mutate(newsletterDraft)
  }

  return (
    <>
      <h5>Neuer Newsletter</h5>
      <div className="tw-flex tw-items-center tw-justify-between tw-my-4">
        <span className="tw-isolate tw-inline-flex tw-rounded-md">
          <button
            type="button"
            className={classNames(
              emailType === "text-only" && "tw-bg-gray-50 tw-ring-gray-500",
              "tw-hover:bg-gray-50 tw-focus:z-10 tw-relative tw-inline-flex tw-items-center tw-rounded-l-md tw-bg-white tw-px-3 tw-py-2 tw-text-sm tw-font-semibold tw-text-gray-900 tw-ring-1 tw-ring-inset tw-ring-gray-300"
            )}
            onClick={() => setEmailType("text-only")}
          >
            Standard
          </button>
          <button
            type="button"
            className={classNames(
              emailType === "products" && "tw-bg-gray-50 tw-ring-gray-500",
              "tw-hover:bg-gray-50 tw-focus:z-10 tw-relative tw-inline-flex tw-items-center tw-rounded-r-md tw-bg-white tw-px-3 tw-py-2 tw-text-sm tw-font-semibold tw-text-gray-900 tw-ring-1 tw-ring-inset tw-ring-gray-300"
            )}
            onClick={() => setEmailType("products")}
          >
            Angebote/Produkte
          </button>
        </span>
        {emailType === "text-only" && (
          <Button disabled={!subject || saveDraftMutation.isPending} onClick={handleSaveDraft}>
            {editingNewsletterId ? "Entwurf aktualisieren" : "Als Entwurf speichern"}
          </Button>
        )}
      </div>
      <Form onSubmit={e => e.preventDefault()}>
        <InputField value={subject} onChange={e => setSubject(e.target.value)} disabled={status !== "default"}>
          Betreff
        </InputField>
        {emailType === "products" && (
          <InputField value={header} onChange={e => setHeader(e.target.value)} disabled={status !== "default"}>
            Überschrift
          </InputField>
        )}
        {emailType === "products" ? (
          <InputField
            rows={8}
            area
            value={body.text}
            onChange={e => setBody({ ...body, text: e.target.value })}
            disabled={status !== "default"}
          >
            Nachricht
          </InputField>
        ) : (
          <Tiptap
            content={body.html}
            setContent={({ html, text }) => setBody({ html, text })}
            relatedCollection={broadcastRef}
          />
        )}
        {emailType === "products" && (
          <InputField
            value={offerHeader}
            onChange={e => setOfferHeader(e.target.value)}
            disabled={status !== "default"}
          >
            Überschrift Angebote
          </InputField>
        )}

        {emailType === "products" && (
          <>
            <SelectedProducts {...{ productIds, setProductIds }} disabled={status !== "default"} />
            <ProductSelector {...{ productIds, setProductIds }} disabled={status !== "default"} />
          </>
        )}
        <div>
          <h6>Checkliste zum Versand</h6>
          <StepComponent steps={steps} />
        </div>
      </Form>
    </>
  )
}

function StepComponent({ steps }: { steps: Step[] }) {
  return (
    <>
      <style jsx>{`
        .stepContainer {
          display: flex;
          flex-direction: row;
          align-items: baseline;
          color: var(--dark);
        }
        .stepContainer.success {
          color: var(--primary);
        }
        .circleNumberContainer {
          font-size: 1rem;
          color: currentColor;
          border: 2px solid currentColor;
          border-radius: 999px;
          font-weight: bold;
          width: 1.8rem;
          height: 1.8rem;
          display: flex;
          text-align: center;
          margin-right: 1rem;
          align-content: center;
          justify-content: center;
        }
        .stepText {
          margin: 0;
          padding: 0.25rem;
          color: currentColor;
          font-weight: bold;
          font-size: 1rem;
        }
        .stepButtonContainer {
          display: grid;
          grid-template-columns: auto auto 1fr;
          grid-gap: 0.25rem;
        }
      `}</style>
      {steps
        .filter(step => step.condition)
        .map((step, index) => {
          const content = step.renderContent ? step.renderContent(step) : step.content
          return (
            <div key={step.id} className={classNames("stepContainer", { success: step.successCondition })}>
              <div className={classNames("circleNumberContainer")}>{index + 1}</div>
              <span className="stepText">{content}</span>
            </div>
          )
        })}
    </>
  )
}
